import { motion } from 'framer-motion';
import { Calendar, TrendingUp, Gift } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
} from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { TokenHandler } from '@/components/auth/TokenHandler';
import { useAuth } from '@/hooks/useAuth';

export const Dashboard: React.FC = () => {
  const { customer, dashboardData, isLoading, error } = useAuth();

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card elevation="md" className="max-w-md">
          <CardBody>
            <div className="text-center">
              <p className="text-error-600 font-medium mb-4">{error}</p>
              <p className="text-neutral-600 text-sm">
                Please request a new login link to access your dashboard.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Show not authenticated state
  if (!customer || !dashboardData) {
    return (
      <>
        <TokenHandler />
        <div className="flex items-center justify-center min-h-screen">
          <Card elevation="md" className="max-w-md">
            <CardBody>
              <div className="text-center">
                <p className="text-neutral-900 font-medium mb-2">Welcome!</p>
                <p className="text-neutral-600 text-sm">
                  Please check your email for your magic link to access your dashboard.
                </p>
              </div>
            </CardBody>
          </Card>
        </div>
      </>
    );
  }

  const activeSubscription = dashboardData.subscriptions.find(s => s.status === 'active');
  const lastOrder = dashboardData.orders[0];

  return (
    <>
      <TokenHandler />
      <div className="space-y-6">
        {/* Hero Section with Gradient */}
        <motion.section
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-warm rounded-2xl p-6 md:p-8"
        >
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl md:text-4xl font-display font-bold text-neutral-900 mb-2">
                Hey {customer.firstName}! 👋
              </h1>
              <p className="text-neutral-700 text-lg">
                Welcome to your customer dashboard
              </p>
            </div>
          </div>

          {/* Quick Stats */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6"
          >
            <motion.div variants={item}>
              <Card padding="sm" variant="glass" elevation="sm">
                <p className="text-xs text-neutral-600 mb-1">Total Orders</p>
                <p className="text-2xl font-bold text-neutral-900">
                  {customer.totalOrders}
                </p>
              </Card>
            </motion.div>

            <motion.div variants={item}>
              <Card padding="sm" variant="glass" elevation="sm">
                <p className="text-xs text-neutral-600 mb-1">Total Spent</p>
                <p className="text-2xl font-bold text-neutral-900">
                  ${typeof customer.totalSpent === 'number' ? customer.totalSpent.toFixed(2) : '0.00'}
                </p>
              </Card>
            </motion.div>

            <motion.div variants={item}>
              <Card padding="sm" variant="glass" elevation="sm">
                <p className="text-xs text-neutral-600 mb-1">Subscriptions</p>
                <p className="text-2xl font-bold text-primary-600">
                  {dashboardData.subscriptions.length}
                </p>
              </Card>
            </motion.div>

            <motion.div variants={item}>
              <Card padding="sm" variant="glass" elevation="sm">
                <p className="text-xs text-neutral-600 mb-1">Active</p>
                <p className="text-2xl font-bold text-success-600">
                  {dashboardData.subscriptions.filter(s => s.status === 'active').length}
                </p>
              </Card>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Subscription Section */}
        {activeSubscription && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card elevation="md" gradient gradientDirection="cool">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>Active Subscription</CardTitle>
                    <CardDescription>
                      Your next delivery is scheduled
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 bg-success-50 text-success-700 px-3 py-1 rounded-full text-sm font-medium">
                    <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse" />
                    Active
                  </div>
                </div>
              </CardHeader>

              <CardBody>
                {/* Next Order Date Circle */}
                <div className="flex items-center gap-6 mb-6">
                  <div className="flex-shrink-0 w-24 h-24 bg-white rounded-full flex flex-col items-center justify-center shadow-medium">
                    <Calendar className="w-6 h-6 text-primary-600 mb-1" />
                    <p className="text-xs text-neutral-600">Next Order</p>
                    <p className="text-lg font-bold text-neutral-900">
                      {new Date(activeSubscription.nextOrderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>

                  <div className="flex-1">
                    <p className="text-sm text-neutral-600 mb-2">
                      Delivery frequency: <span className="font-medium text-neutral-900">{activeSubscription.frequency}</span>
                    </p>
                    <p className="text-sm text-neutral-600">
                      Status: <span className="font-medium text-success-600">{activeSubscription.status}</span>
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button variant="primary" gradient className="flex-1">
                    Manage Subscription
                  </Button>
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* Last Order Section */}
        {lastOrder && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card elevation="md">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle>Last Order #{lastOrder.orderNumber}</CardTitle>
                    <CardDescription>
                      Placed on {new Date(lastOrder.createdAt).toLocaleDateString()}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2 bg-info-50 text-info-700 px-3 py-1 rounded-full text-sm font-medium">
                    <TrendingUp className="w-4 h-4" />
                    {lastOrder.fulfillmentStatus || 'Pending'}
                  </div>
                </div>
              </CardHeader>

              <CardBody>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-600">Total:</span>
                    <span className="font-medium text-neutral-900">${lastOrder.totalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-neutral-600">Payment Status:</span>
                    <span className="font-medium text-success-600">{lastOrder.financialStatus}</span>
                  </div>
                </div>

                <Button variant="outline" fullWidth>
                  View Order Details
                </Button>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* Affiliate Stats */}
        {dashboardData.affiliateStats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card elevation="md" gradient gradientDirection="accent">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Gift className="w-6 h-6 text-accent-600" />
                  <CardTitle>Affiliate Program</CardTitle>
                </div>
                <CardDescription>
                  Your referral code: {dashboardData.affiliateStats.referralCode}
                </CardDescription>
              </CardHeader>
              <CardBody>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-neutral-600 mb-1">Total Sales</p>
                    <p className="text-xl font-bold text-neutral-900">
                      {dashboardData.affiliateStats.totalSales}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-600 mb-1">Total Earned</p>
                    <p className="text-xl font-bold text-success-600">
                      ${dashboardData.affiliateStats.totalEarnings.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-600 mb-1">Pending</p>
                    <p className="text-xl font-bold text-warning-600">
                      ${dashboardData.affiliateStats.pendingEarnings.toFixed(2)}
                    </p>
                  </div>
                </div>
                <Button variant="secondary" fullWidth>
                  Share Your Link
                </Button>
              </CardBody>
            </Card>
          </motion.div>
        )}
      </div>
    </>
  );
};

export default Dashboard;
